import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import * as Promise from 'bluebird';
import { Component } from 'react';
import {
  ColumnDefinition,
  getFilters,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter,
  getPagesCount,
  isFilterExist,
  isSortExist,
  TABLE_SORT_DIRECTION,
} from 'Src/helpers/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Loader } from 'Src/components/Loader/Loader';
import { Grid } from 'Src/components/Grid/Grid';
import { addUser, deleteUser, getAllUsers, getUsers, getUsersCount, updateUser } from 'Src/services/superadmin/users';
import { User } from 'Src/models/users';
import { UserForm } from './UserForm/UserForm';
import { SimpleModal } from '../../../../../../components/SimpleModal/SimpleModal';
import { getUserMenuItems, getUserType } from '../../../../../../helpers/accessor';
import { getSelectOptionForUserMenuItems, getSelectOptionForUserTypes } from '../../../../../../helpers/select';
import { USER_TYPE } from '../../../../../../consts/user';
import { MenuItemsForm } from './MenuItemsForm/MenuItemsForm';
import * as propz from 'propz';
import { convertToFilterWithPlus } from '../../../../../../helpers/table';
import { LIMIT } from '../../../../../../consts/table';
import { CustomSendTestNotificationForm } from '../Customers/CustomerNotificationChannels/CustomerSendNotificationForm/CustomerSendTestNotificationForm';
import { sendCustomerNotification } from '../../../../../../services/superadmin/customers';
import { sendAdminNotification } from '../../../../../../services/superadmin/admins';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Email',
    field: 'email',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['email'],
  },
  {
    text: 'Type',
    field: 'userTypes',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserType,
  },
  {
    text: 'Menu items',
    field: 'menuItems',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserMenuItems,
  },
];

interface State {
  items: User[];
  currentPage: number;
  selectedItems: User[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  isUserFormModalOpen: boolean;
  isMenuItemsFormModalOpen: boolean;
  isConfirmationModalOpen: boolean;
  isErrorModalOpen: boolean;
  errorMessage: string;

  isSendNotificationFormModalOpen: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Users extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      isUserFormModalOpen: false,
      isMenuItemsFormModalOpen: false,
      isConfirmationModalOpen: false,
      isErrorModalOpen: false,
      errorMessage: '',

      isSendNotificationFormModalOpen: false,
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true,
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true,
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters(COLUMNS, search);

    const isShowFilter = isFilterExist(filters);
    const isDataFiltered = isFilterExist(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter(page, where, order);

    const getItemsPromise = getUsers(user, serverQueryFilter);
    const getItemsCountPromise = getUsersCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return Promise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection as TABLE_SORT_DIRECTION,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false,
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const selectedItemsNext = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: selectedItemsNext,
      isSelectAllChecked: true,
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true,
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getAllUsers(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const selectedItemsNext = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: selectedItemsNext,
        isSelectAllChecked: true,
        isLoading: false,
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue,
            },
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue,
            },
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value,
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue,
        };
      }
    }

    this.setState({
      filters: nextFilters,
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    const filtersConverted = convertToFilterWithPlus(filters);

    if (isFilter) {
      search.push(getSearchFilter(filtersConverted));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: '',
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter,
    });
  };

  onCloseErrorClick = () => {
    this.setState({
      isErrorModalOpen: false,
      errorMessage: '',
      isLoading: true,
    });
    this.setItems();
  };

  onSubmitForm = data => {
    const { selectedItems, total } = this.state;
    const { user } = this.props;

    this.setState({
      isUserFormModalOpen: false,
      isLoading: true,
    });

    if (selectedItems.length === 1) {
      const userUpdated = selectedItems[0];
      const { id } = userUpdated;

      updateUser(user, id, data)
        .then(res => {
          this.setState({
            selectedItems: [],
            isSelectAllChecked: false,
          });
          this.setItems();
        })
        .catch(error => {
          const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
          this.setState({
            isLoading: false,
            isErrorModalOpen: true,
            errorMessage: errorText,
          });
          console.error('error');
          console.error(errorText);
        });
    } else {
      addUser(user, data)
        .then(res => {
          const lastPage = getPagesCount(total + 1, LIMIT);
          this.setState({
            selectedItems: [],
            isSelectAllChecked: false,
          });
          this.setCurrentPageParams(lastPage);
          this.setItems();
        })
        .catch(error => {
          const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
          this.setState({
            isLoading: false,
            isErrorModalOpen: true,
            errorMessage: errorText,
          });
          console.error('error');
          console.error(errorText);
        });
    }
  };

  onCancelButtonClick = () => {
    this.setState({
      isUserFormModalOpen: false,
    });
  };

  onCreateUserClick = () => {
    this.setState({
      isUserFormModalOpen: true,
    });
  };

  onEditUserClick = () => {
    this.setState({
      isUserFormModalOpen: true,
    });
  };

  renderSendingError(): React.ReactNode {
    const { errorMessage, isErrorModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isErrorModalOpen}
        title={'Error'}
        body={errorMessage}
        buttonCancelText={'Ok'}
        onCloseClick={this.onCloseErrorClick}
      />
    );
  }

  renderFormModal(): React.ReactNode {
    const { isUserFormModalOpen, selectedItems } = this.state;

    if (selectedItems.length === 1) {
      const user = selectedItems[0];
      return (
        <SimpleModal isOpen={isUserFormModalOpen} title={'Edit user'}>
          <UserForm user={user} onCancel={this.onCancelButtonClick} onSubmit={this.onSubmitForm} />
        </SimpleModal>
      );
    } else {
      return (
        <SimpleModal isOpen={isUserFormModalOpen} title={'Create user'}>
          <UserForm onCancel={this.onCancelButtonClick} onSubmit={this.onSubmitForm} />
        </SimpleModal>
      );
    }
  }

  onDeleteUserClick = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
      isConfirmationModalOpen: false,
    });

    Promise.all(selectedItems.map(selectedItem => deleteUser(user, selectedItem.id))).then(res => {
      this.setState({
        selectedItems: [],
        isSelectAllChecked: false,
      });

      this.setItems();
    });
  };

  showConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: true,
    });
  };

  hideConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: false,
    });
  };

  renderConfirmationModal(): React.ReactNode {
    const { isConfirmationModalOpen, selectedItems } = this.state;

    return (
      <SimpleModal
        isOpen={isConfirmationModalOpen}
        title={'Confirmation'}
        body={`Are you sure you want to remove ${selectedItems.length === 1 ? 'user' : 'users'}?`}
        buttonText={'Remove'}
        buttonCancelText={'Cancel'}
        onButtonClick={this.onDeleteUserClick}
        onCloseClick={this.hideConfirmationModal}
      />
    );
  }

  onEditMenuItemsClick = () => {
    this.setState({
      isMenuItemsFormModalOpen: true,
    });
  };

  onCancelMenuItemsButtonClick = () => {
    this.setState({
      isMenuItemsFormModalOpen: false,
    });
  };

  onSubmitMenuItemsButtonClick = data => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
      isMenuItemsFormModalOpen: false,
    });

    Promise.all(selectedItems.map(selectedItem => updateUser(user, selectedItem.id, { menuItemsEnabled: data }))).then(
      items => {
        this.setState({
          selectedItems: items,
        });

        this.setItems();
      }
    );
  };

  renderMenuItemsFormModal(): React.ReactNode {
    const { isMenuItemsFormModalOpen, selectedItems } = this.state;

    return (
      <SimpleModal isOpen={isMenuItemsFormModalOpen} title={'Edit menu items'}>
        <MenuItemsForm
          onCancel={this.onCancelMenuItemsButtonClick}
          onSubmit={this.onSubmitMenuItemsButtonClick}
          users={selectedItems}
        />
      </SimpleModal>
    );
  }

  isEditMenuItemsActive(): boolean {
    const { selectedItems } = this.state;
    const { user } = this.props;
    const isEveryAdmins = selectedItems.every(item => item.type === USER_TYPE.ADMIN);

    return selectedItems.length > 0 && isEveryAdmins;
  }

  onCancelSendNotificationButtonClick = () => {
    this.setState({
      isSendNotificationFormModalOpen: false,
    });
  };

  onSendTestNotificationButtonClick = () => {
    this.setState({
      isSendNotificationFormModalOpen: true,
    });
  };

  onSendNotificationForm = data => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    const admin = selectedItems[0];
    const adminId = admin.id;

    const { notificationChannels } = admin;

    const checkedChannelId = notificationChannels[0].id;

    this.setState({
      isLoading: true,
    });

    sendAdminNotification(user, adminId, checkedChannelId, data).then(res => {
      this.setState({
        isSendNotificationFormModalOpen: false,
        isLoading: false,
      });
    });
  };

  renderSendTestNotificationFormModal(): React.ReactNode {
    const { isSendNotificationFormModalOpen, selectedItems } = this.state;
    if (selectedItems.length === 1) {
      return (
        <SimpleModal isOpen={isSendNotificationFormModalOpen} title={'Send a test notification'}>
          <CustomSendTestNotificationForm
            onCancel={this.onCancelSendNotificationButtonClick}
            onSubmit={this.onSendNotificationForm}
          />
        </SimpleModal>
      );
    }
  }

  render() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isLoading,
      isSelectAllChecked,
      currentPage,
      total,
      isUserFormModalOpen,
      isConfirmationModalOpen,
      isMenuItemsFormModalOpen,
      isErrorModalOpen,
      isSendNotificationFormModalOpen,
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isUserFormModalOpen ||
      isConfirmationModalOpen ||
      isMenuItemsFormModalOpen ||
      isErrorModalOpen ||
      isSendNotificationFormModalOpen
        ? 'mt-3 modal-open'
        : 'mt-3';

    const actionItems = [
      {
        itemText: 'Create user',
        onItemClick: this.onCreateUserClick,
        isActive: selectedItems.length === 0,
      },
      {
        itemText: 'Edit user',
        onItemClick: this.onEditUserClick,
        isActive: selectedItems.length === 1,
      },
      {
        itemText: selectedItems.length === 1 ? 'Delete user' : 'Delete users',
        onItemClick: this.showConfirmationModal,
        isActive: selectedItems.length > 0,
      },
      {
        itemText: 'Edit menu items',
        onItemClick: this.onEditMenuItemsClick,
        isActive: this.isEditMenuItemsActive(),
      },
      // for test purpose
      // {
      //   itemText: 'Send test email notification',
      //   onItemClick: this.onSendTestNotificationButtonClick,
      //   isActive: selectedItems.length === 1,
      // },
    ];

    const filterOptions = {
      userTypes: getSelectOptionForUserTypes(),
      menuItems: getSelectOptionForUserMenuItems(),
    };

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderFormModal()}
        {this.renderMenuItemsFormModal()}
        {this.renderConfirmationModal()}
        {this.renderSendingError()}
        {this.renderSendTestNotificationFormModal()}
        <div className="row">
          <div className="col-md-12">
            <Grid
              dataItems={items}
              filters={filters}
              currentPage={currentPage}
              total={total}
              isSelectAllChecked={isSelectAllChecked}
              isDataFiltered={isDataFiltered}
              sortDirection={sortDirection}
              sortColumnsName={sortColumnsName}
              isShowFilter={isShowFilter}
              dataItemsSelected={selectedItems}
              columns={COLUMNS}
              actionItems={actionItems}
              options={filterOptions}
              onItemClick={this.onItemClick}
              onSortClick={this.onTableSortClick}
              onApplyFilterClick={this.onApplyFilterClick}
              onClearFilterClick={this.onClearFilterClick}
              onTableFilterChange={this.onTableFilterChange}
              onTableFilterClick={this.onTableFilterClick}
              setCurrentPageParams={this.setCurrentPageParams}
              onSelectAllClick={this.onSelectAllClick}
              onSelectAllOnPageClick={this.onSelectAllOnPageClick}
              onUnselectAllClick={this.onUnselectAllClick}
            />
          </div>
        </div>
      </div>
    );
  }
}
